import { List } from 'immutable'
import React, { ReactElement, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { useEffectOnce, usePrevious } from 'react-use'

import { addAlertSignature } from '../../../actions/alerts'
import paths from '../../../constants/paths'
import { CostCenterAccounting } from '../../../model/accountingIntegration'
import Company from '../../../model/company'
import CostCenter from '../../../model/costCenter'
import Employee from '../../../model/employee'
import { OneTimePayCreationFields, OneTimePayMutableFields, OneTimePayType } from '../../../model/oneTimePay'
import SalaryCycle from '../../../model/salaryCycle'
import { CompanyAccountPlanReducer } from '../../../reducers/companyAccountPlans'
import { OneTimePayReducer } from '../../../reducers/oneTimePays'
import { formatDate } from '../../../utils/date-utils'
import { formatCurrency } from '../../../utils/number-utils'
import { t } from '../../../utils/translation-utils'
import Button from '../../elements/button'
import Card from '../../elements/card'
import Icon from '../../elements/icon'
import Modal from '../../elements/modal'
import Table from '../../elements/table'
import Title from '../../elements/Title'
import TitleMenu from '../../elements/TitleMenu'
import Tooltip from '../../elements/tooltip'
import DumbLink from '../../widgets/DumbLink'
import LoadingOverlay from '../../widgets/LoadingOverlay'
import FeeEdit from './FeeEdit'

type Props = {
  subsection?: string
  canEditObjects: boolean
  canApproveObjects: boolean
  employee: Employee
  company: Company
  oneTimePays: OneTimePayReducer
  salaryCycle?: SalaryCycle
  costCenters: List<CostCenter>
  costCenterAccounting: CostCenterAccounting
  companyAccountPlans: CompanyAccountPlanReducer

  addAlert: addAlertSignature
  approveOneTimePays: (ids: string[]) => void
  unapproveOneTimePays: (ids: string[]) => void
  addOneTimePay: (employeeID: string, oneTimePay: OneTimePayCreationFields) => void
  updateOneTimePay: (employeeID: string, oneTimePay: OneTimePayMutableFields) => void
  deleteOneTimePay: (id: string) => void
  getCompanyAccountPlans: () => void
}

export default function FeeTab(props: Props): ReactElement | null {
  const [approving, setApproving] = useState<string[]>([])
  const [deleting, setDeleting] = useState<string[]>([])
  const [modalKey, setModalKey] = useState(1)
  const [editing, setEditing] = useState<string | boolean>(false)
  const [mutable, setMutable] = useState(false)

  const { companyAccountPlans, getCompanyAccountPlans } = props

  useEffectOnce(() => {
    if (!companyAccountPlans.loading && !companyAccountPlans.loaded) {
      getCompanyAccountPlans()
    }
  })

  const setEditVisibility = (id: string | boolean, mutable = true) => {
    // Increment modalKey to create a new component
    setModalKey((prev) => prev + 1)
    setEditing(id)
    setMutable(mutable)
  }

  const { oneTimePays } = props
  const previousOneTimePays = usePrevious(oneTimePays)

  useEffect(() => {
    // Check for save callback
    if (previousOneTimePays && previousOneTimePays.saving && !oneTimePays.saving) {
      // Check for no error occurred
      if (!oneTimePays.error) {
        // Close edit modal
        setEditVisibility(false)
      }
    }
  })

  const approve = (oneTimePayID: string) => {
    return (e: React.MouseEvent) => {
      e.preventDefault()
      setApproving((prev) => [...prev, oneTimePayID])
      props.approveOneTimePays([oneTimePayID])
    }
  }
  const unapprove = (oneTimePayID: string) => {
    return (e: React.MouseEvent) => {
      e.preventDefault()
      setApproving((prev) => prev.filter((id) => id !== oneTimePayID))
      props.unapproveOneTimePays([oneTimePayID])
    }
  }
  const remove = (oneTimePayID: string) => {
    return (e: React.MouseEvent) => {
      e.preventDefault()
      if (window.confirm(t('common.are_you_sure'))) {
        setDeleting((prev) => [...prev, oneTimePayID])
        props.deleteOneTimePay(oneTimePayID)
      }
    }
  }

  type OneTimePayRow = {
    key: string
    id: string
    date: string
    amount: string
    title: string
    approved: boolean
    immutable: boolean
    deletable: boolean
    withPayment: boolean
  }

  const columns = [
    {
      title: t('freelancer.fee.table.header.approved'),
      key: 'x1',
      className: 'one-time-pay-table-approved',
      render: (oneTimePay: OneTimePayRow) => {
        if (oneTimePay.approved) {
          return (
            <span className="table-approve-wrapper">
              {t('freelancer.fee.table.approved.true')}
              {!oneTimePay.immutable && props.canApproveObjects && (
                <Tooltip title={t('freelancer.fee.table.approved.remove')}>
                  <span onClick={unapprove(oneTimePay.id)} style={{ cursor: 'pointer' }}>
                    <Icon type="xSign" />
                  </span>
                </Tooltip>
              )}
            </span>
          )
        }
        if (approving.indexOf(oneTimePay.id) !== -1) {
          return t('freelancer.fee.table.saving')
        }
        if (!props.canApproveObjects) {
          return t('freelancer.fee.table.approved.false')
        }
        return (
          <DumbLink onClick={approve(oneTimePay.id)} type="standard">
            {t('freelancer.fee.table.approved.approve')}
          </DumbLink>
        )
      },
    },
    { title: t('freelancer.fee.table.header.date'), dataIndex: 'date', key: 'date' },
    { title: t('freelancer.fee.table.header.title'), dataIndex: 'title', key: 'title' },
    { title: t('freelancer.fee.table.header.amount'), dataIndex: 'amount', key: 'amount' },
    {
      title: t('freelancer.fee.table.header.with_payment'),
      key: 'x2',
      render: (oneTimePay: OneTimePayRow) => (oneTimePay.withPayment ? 'Nej' : 'Ja'),
    },
    {
      title: '',
      key: 'x3',
      className: 'employee-table-actions',
      render: (oneTimePay: OneTimePayRow) => {
        if (deleting.indexOf(oneTimePay.id) !== -1) {
          return null
        }
        if (!props.canEditObjects) {
          return null
        }
        return (
          <div>
            <Tooltip
              title={
                oneTimePay.immutable ? t('freelancer.fee.table.actions.view') : t('freelancer.fee.table.actions.edit')
              }
            >
              <span
                onClick={() => setEditVisibility(oneTimePay.id, !oneTimePay.immutable)}
                style={{ cursor: 'pointer' }}
              >
                <Icon type={oneTimePay.immutable ? 'magnifyingGlass' : 'paperWithPencil'} />
              </span>
            </Tooltip>
            {oneTimePay.deletable && (
              <Tooltip title={t('freelancer.fee.table.actions.delete')}>
                <span onClick={remove(oneTimePay.id)} style={{ cursor: 'pointer' }}>
                  <Icon type="xSign" />
                </span>
              </Tooltip>
            )}
          </div>
        )
      },
    },
  ]

  const showHistory = () => {
    return props.subsection === 'history'
  }

  const getFees = (): OneTimePayRow[] => {
    const types: OneTimePayType[] = ['Compensation', 'Honorary Gift', 'Grant', 'Compensation No AM']
    return props.oneTimePays.oneTimePays
      .filter((oneTimePay) => types.indexOf(oneTimePay.type) !== -1)
      .filter((oneTimePay) => (showHistory() ? oneTimePay.settled : !oneTimePay.settled))
      .map((oneTimePay) => {
        let title = oneTimePay.title
        if (title.length > 22) {
          title = title.substring(0, 20) + '...'
        }
        return {
          key: oneTimePay.id,
          id: oneTimePay.id,
          date: formatDate(oneTimePay.dispositionDate),
          amount: formatCurrency(oneTimePay.amount),
          withPayment: oneTimePay.payOutType !== 'Manual',
          title,
          approved: oneTimePay.approved,
          immutable: oneTimePay.immutable,
          deletable: oneTimePay.deletable,
        }
      })
      .toArray()
  }

  const approveAll = (e: React.MouseEvent) => {
    e.preventDefault()
    if (window.confirm(t('common.are_you_sure'))) {
      const oneTimePays = getFees().filter((reg) => !reg.approved)
      setApproving(oneTimePays.map((otp) => otp.id))
      props.approveOneTimePays(oneTimePays.map((otp) => otp.id))
    }
  }

  const hasUnapprovedFees = () => {
    return getFees().some((otp) => !otp.approved)
  }

  if (!props.salaryCycle) {
    return null
  }

  return (
    <Card className="employees-single-form">
      <TitleMenu>
        {!showHistory() && props.canEditObjects && (
          <Button onClick={() => setEditVisibility(true)} style={{ marginRight: 20 }} prefixIcon="user">
            {t('freelancer.fee.header.new_fee')}
          </Button>
        )}
        {showHistory() ? (
          <Link to={'/' + paths.FREELANCERS + '/' + props.employee.id + '/overview'}>
            <Button className="gtm-hide-bonus-history" style={{ paddingRight: 14 }}>
              {t('freelancer.fee.header.hide_history')}
            </Button>
          </Link>
        ) : (
          <Link to={'/' + paths.FREELANCERS + '/' + props.employee.id + '/overview/history'}>
            <Button className="gtm-show-bonus-history" style={{ paddingRight: 14 }}>
              {t('freelancer.fee.header.show_history')}
            </Button>
          </Link>
        )}

        {hasUnapprovedFees() && props.canApproveObjects && (
          <Button onClick={approveAll} style={{ marginLeft: 20 }}>
            {t('freelancer.fee.header.approve_all')}
          </Button>
        )}
      </TitleMenu>
      <Title>{t('freelancer.fee.header.title')}</Title>
      <p>&nbsp;</p>

      <Table columns={columns} dataSource={getFees()} pagination={false} />
      {props.oneTimePays.saving && <LoadingOverlay />}

      <Modal
        key={modalKey}
        visible={editing !== false}
        onOk={() => setEditVisibility(false)}
        onCancel={() => setEditVisibility(false)}
        width={582}
        footer={null}
      >
        <FeeEdit
          visible={editing !== false}
          editing={mutable}
          canApproveObjects={props.canApproveObjects}
          employee={props.employee}
          company={props.company}
          oneTimePayID={typeof editing === 'string' ? editing : undefined}
          oneTimePays={props.oneTimePays}
          salaryCycle={props.salaryCycle}
          costCenters={props.costCenters}
          costCenterAccounting={props.costCenterAccounting}
          accounts={props.companyAccountPlans.accounts}
          addOneTimePay={props.addOneTimePay}
          updateOneTimePay={props.updateOneTimePay}
        />
      </Modal>
    </Card>
  )
}
